import React, { useState, useEffect } from 'react';
import api from '../../api';
import { imgUrl } from '../../utils';
import './Admin.css';

const EMPTY = { name:'', description:'', category_id:'', price:'', min_order_qty:1, stock_status:'available', sizes:'', colours:'', featured:false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // Fix #13
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [p, c] = await Promise.all([api.get('/admin/products'), api.get('/categories')]);
    setProducts(p.data);
    const flat = [];
    c.data.forEach(parent => { parent.subcategories?.forEach(sub => flat.push({ ...sub, parentName: parent.name })); });
    setCategories(flat);
    setAllCats(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setFiles([]); setExistingImages([]); setModal('add'); };
  const openEdit = async (p) => {
    setForm({ name:p.name, description:p.description||'', category_id:p.category_id, price:p.price||'', min_order_qty:p.min_order_qty||1, stock_status:p.stock_status||'available', sizes:p.sizes||'', colours:p.colours||'', featured:p.featured||false, _id:p._id });
    setFiles([]);
    setMsg('');
    // Fix #13: load existing images for this product
    try {
      const r = await api.get(`/admin/products/${p._id}/images`);
      setExistingImages(r.data);
    } catch { setExistingImages([]); }
    setModal('edit');
  };
  const closeModal = () => { setModal(null); setMsg(''); setExistingImages([]); };

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  // Fix #13: delete an existing image
  const deleteImage = async (imgId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/products/${form._id}/images/${imgId}`);
      setExistingImages(prev => prev.filter(i => i._id !== imgId));
    } catch(e) { alert('Could not delete image'); }
  };

  // Fix #13: set an image as primary
  const setPrimary = async (imgId) => {
    try {
      await api.patch(`/admin/products/${form._id}/images/${imgId}/primary`);
      setExistingImages(prev => prev.map(i => ({ ...i, is_primary: i._id === imgId })));
    } catch(e) { alert('Could not update primary image'); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if(k!=='_id') fd.append(k, v); });
      files.forEach(f => fd.append('images', f));
      if (modal === 'edit' && form._id) await api.patch(`/admin/products/${form._id}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      else await api.post('/admin/products', fd, { headers:{'Content-Type':'multipart/form-data'} });
      setMsg('Saved!'); load(); setTimeout(closeModal, 800);
    } catch(err) { setMsg(err.response?.data?.error||'Error saving'); }
    setSaving(false);
  };

  const del = async (id) => { if(window.confirm('Delete this product and all its images?')) { await api.delete(`/admin/products/${id}`); load(); }};

  const flatCatName = (p) => `${p.parent_name ? p.parent_name+' › ' : ''}${p.category_name||''}`;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Products</h1>
        <button className="btn-terracotta btn-sm" onClick={openAdd}>+ Add Product</button>
      </div>
      {loading ? <div className="page-loading"><div className="spinner"/></div> : (
        <table className="admin-table">
          <thead><tr><th>Image</th><th>Name</th><th>Category</th><th>Price</th><th>Min Qty</th><th>Status</th><th>Featured</th><th>Actions</th></tr></thead>
          <tbody>
            {products.map(p => (
              <tr key={p._id}>
                <td style={{width:'64px'}}>
                  {p.primary_image
                    ? <img src={imgUrl(p.primary_image)} alt="" style={{width:'56px',height:'56px',objectFit:'cover'}}/>
                    : <div style={{width:'56px',height:'56px',background:'var(--cream-darker)'}}/>
                  }
                </td>
                <td><strong>{p.name}</strong></td>
                <td style={{fontSize:'12px',color:'var(--gray)'}}>{flatCatName(p)}</td>
                <td>{p.price ? `£${Number(p.price).toFixed(2)}` : <span style={{color:'var(--gray)'}}>—</span>}</td>
                <td>{p.min_order_qty}</td>
                <td><span className={`badge ${p.stock_status==='available'?'badge-success':p.stock_status==='coming_soon'?'badge-warning':'badge-error'}`}>{p.stock_status}</span></td>
                <td>{p.featured ? '★' : '—'}</td>
                <td>
                  <div className="admin-actions">
                    <button className="admin-btn admin-btn-edit" onClick={() => openEdit(p)}>Edit</button>
                    <button className="admin-btn admin-btn-delete" onClick={() => del(p._id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length===0 && <tr><td colSpan="8" style={{textAlign:'center',padding:'32px',color:'var(--gray)'}}>No products yet. Add your first product above.</td></tr>}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&closeModal()}>
          <div className="modal">
            <button className="modal-close" onClick={closeModal}>✕</button>
            <h2 className="modal-title">{modal==='add'?'Add New Product':'Edit Product'}</h2>
            {msg && <div className={`alert ${msg==='Saved!'?'alert-success':'alert-error'}`}>{msg}</div>}
            <form onSubmit={save} style={{display:'flex',flexDirection:'column',gap:'0'}}>
              <div className="form-group"><label>Product Name *</label><input required value={form.name} onChange={e=>set('name',e.target.value)} /></div>
              <div className="form-group">
                <label>Category *</label>
                <select required value={form.category_id} onChange={e=>set('category_id',e.target.value)}>
                  <option value="">Select sub-category</option>
                  {allCats.map(parent => (
                    <optgroup key={parent._id} label={parent.name}>
                      {parent.subcategories?.map(sub => <option key={sub._id} value={sub._id}>{sub.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="form-group"><label>Description</label><textarea rows={3} value={form.description} onChange={e=>set('description',e.target.value)} /></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                <div className="form-group"><label>Wholesale Price (£)</label><input type="number" step="0.01" value={form.price} onChange={e=>set('price',e.target.value)} placeholder="0.00" /></div>
                <div className="form-group"><label>Min. Order Qty *</label><input required type="number" min="1" value={form.min_order_qty} onChange={e=>set('min_order_qty',parseInt(e.target.value)||1)} /></div>
                <div className="form-group"><label>Sizes</label><input value={form.sizes} onChange={e=>set('sizes',e.target.value)} placeholder="S, M, L, XL or One Size" /></div>
                <div className="form-group"><label>Colours</label><input value={form.colours} onChange={e=>set('colours',e.target.value)} placeholder="e.g. Beige/Red" /></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                <div className="form-group">
                  <label>Stock Status</label>
                  <select value={form.stock_status} onChange={e=>set('stock_status',e.target.value)}>
                    <option value="available">Available</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:'12px',paddingTop:'28px'}}>
                  <input type="checkbox" id="featured" checked={form.featured} onChange={e=>set('featured',e.target.checked)} style={{width:'auto'}}/>
                  <label htmlFor="featured" style={{textTransform:'none',letterSpacing:0,fontSize:'14px',marginBottom:0,color:'var(--charcoal)'}}>Featured on homepage</label>
                </div>
              </div>
              {/* Fix #13: Show and manage existing images when editing */}
              {modal === 'edit' && existingImages.length > 0 && (
                <div className="form-group">
                  <label>Current Images</label>
                  <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginTop:'8px'}}>
                    {existingImages.map(img => (
                      <div key={img._id} style={{position:'relative',width:'80px'}}>
                        <img src={imgUrl(img.filename)} alt="" style={{width:'80px',height:'80px',objectFit:'cover',border:`2px solid ${img.is_primary?'var(--terracotta)':'var(--gray-light)'}`}} />
                        {img.is_primary && <div style={{position:'absolute',top:2,left:2,background:'var(--terracotta)',color:'white',fontSize:'8px',padding:'1px 4px',letterSpacing:'0.5px'}}>PRIMARY</div>}
                        <div style={{display:'flex',gap:'2px',marginTop:'4px'}}>
                          {!img.is_primary && <button type="button" onClick={()=>setPrimary(img._id)} style={{flex:1,fontSize:'9px',padding:'3px',background:'var(--cream-dark)',border:'1px solid var(--gray-light)',cursor:'pointer'}} title="Set as primary">★</button>}
                          <button type="button" onClick={()=>deleteImage(img._id)} style={{flex:1,fontSize:'9px',padding:'3px',background:'#fdecea',border:'1px solid #f5c6c6',color:'var(--error)',cursor:'pointer'}} title="Delete">✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{fontSize:'11px',color:'var(--gray)',marginTop:'6px'}}>★ = set as primary (shown on catalogue) &nbsp;|&nbsp; ✕ = delete image</p>
                </div>
              )}
              <div className="form-group">
                <label>{modal === 'edit' ? 'Add More Images' : 'Product Images'}</label>
                <input type="file" multiple accept="image/*" onChange={e=>setFiles(Array.from(e.target.files))} style={{padding:'8px'}}/>
                {files.length > 0 && <p style={{fontSize:'12px',color:'var(--gray)',marginTop:'6px'}}>{files.length} new file(s) selected</p>}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary btn-sm" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-terracotta btn-sm" disabled={saving}>{saving?'Saving...':'Save Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
